'use client'

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { IpfsImage } from '@/components/ui/ipfs-image'
import { RoundSpinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { updateProfile } from '@/lib/api/user/set-username'
import { useUserStore } from '@/lib/context/user-context'
import { catchable } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { useFilePicker } from 'use-file-picker'
import { z } from 'zod'
import { Button } from '../../ui/button'
import { InputWrapper } from '../../ui/input-label-wrapper'
import { useUserProvider } from '@/lib/context/user-provider'
import Image from 'next/image'

const FormSchema = z.object({
  name: z.string().min(3, { message: 'Username must be at least 3 characters' }).max(20, { message: 'Username must be at most 20 characters' }),
  description: z.string().optional().nullable(),
  avatarUrl: z.string().optional(),
  twitterUrl: z.string().url({ message: 'Invalid URL format' }).or(z.literal('')).optional(),
  telegramUrl: z.string().url({ message: 'Invalid URL format' }).or(z.literal('')).optional(),
  websiteUrl: z.string().url({ message: 'Invalid URL format' }).or(z.literal('')).optional(),
})

type FormValues = z.infer<typeof FormSchema>

export default function EditProfileDialog({ children }: { children: ReactNode }) {
  const { updateProfile, user } = useUserProvider();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>(
    `${process.env.NEXT_PUBLIC_API}/public/${user?.avatarUrl}`
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: user?.name || '',
      description: user?.description || '',
      avatarUrl: user?.avatarUrl || '',
      twitterUrl: user?.twitterUrl || '',
      telegramUrl: user?.telegramUrl || '',
      websiteUrl: user?.websiteUrl || '',
    },
  });

  const {
    openFilePicker,
    plainFiles,
    filesContent,
  } = useFilePicker({ accept: ['image/*'], multiple: false });

  useEffect(() => {
    if (isOpen && user) {
      form.reset({
        name: user.name || '',
        description: user.description || '',
        avatarUrl: user.avatarUrl || '',
        twitterUrl: user.twitterUrl || '',
        telegramUrl: user.telegramUrl || '',
        websiteUrl: user.websiteUrl || '',
      });
      setAvatarUrl(`${process.env.NEXT_PUBLIC_API}/public/${user?.avatarUrl}`);
    }
  }, [isOpen, user, form.reset]);

  const handleSubmit = form.handleSubmit(async values => {
    const updatedData : any = {}


    if (values.name !== user?.name) updatedData.name = values.name
    if (values.description !== user?.description) updatedData.description = values.description
    if (values.twitterUrl !== user?.twitterUrl) updatedData.twitterUrl = values.twitterUrl
    if (values.telegramUrl !== user?.telegramUrl) updatedData.telegramUrl = values.telegramUrl
    if (values.websiteUrl !== user?.websiteUrl) updatedData.websiteUrl = values.websiteUrl

    if (Object.keys(updatedData).length > 0 || selectedFile) {
      console.log(selectedFile)
      const result = await updateProfile(updatedData, selectedFile ? selectedFile : undefined)
      if (result) {
        toast.success('Profile updated successfully!')
        setIsOpen(false)
      } else {
        toast.error('Profile update failed!')
      }
    }
  })



  const handleFileSelect = async () => {
    await openFilePicker(); 

   
  };

  useEffect(() => {
 if (plainFiles.length > 0) {

      setSelectedFile(plainFiles[0]);




      const objectUrl = URL.createObjectURL(plainFiles[0]);

      console.log(objectUrl)

      setAvatarUrl(objectUrl);
    }
  }, [plainFiles])

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className={'bg-card border-none'}>
        <DialogHeader>
          <DialogTitle>Edit profile</DialogTitle>
        </DialogHeader>
        <div className={'flex items-center justify-start gap-2'}>
          <Image
            className='rounded-md'
            src={avatarUrl}
            width={52}
            height={52}
            alt="User avatar"
          />
          <button className={'text-primary text-sm'} onClick={handleFileSelect}>
            Edit
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={'mt-6 space-y-3'}>

            <div>
              <label className={'text-xs text-tertiary'}>Username</label>
              <Input
                placeholder={'Enter username (3-20 chars)'}
                className={'rounded-lg'}
                {...form.register('name')}
              />
              <p className={'text-xs font-roboto-mono text-destructive mt-1'}>
                {form.formState.errors.name?.message}
              </p>
            </div>

            <div>
              <label className={'text-xs text-tertiary'}>Description</label>
              <Textarea
                placeholder={'Short description about you (optional)'}
                className={'resize-none h-[100px] rounded-lg'}
                {...form.register('description')}
              />
              <p className={'text-xs font-roboto-mono text-destructive mt-1'}>
                {form.formState.errors.description?.message}
              </p>
            </div>
            <div className='grid grid-cols-2 gap-4'>

            <InputWrapper label='Telegram Url' error=  {form.formState.errors.telegramUrl?.message}>
              <Input
                  placeholder={'Telegram profile or channel URL'}
                  className={'rounded-lg'}
                  {...form.register('telegramUrl')}
                />
            </InputWrapper>
            <InputWrapper label='Website Url' error=  {form.formState.errors.websiteUrl?.message}>
              <Input
                  placeholder={'Your website URL (optional)'}
                  className={'rounded-lg'}
                  {...form.register('websiteUrl')}
                />
            </InputWrapper>
            <InputWrapper label='Twitter Url' error=  {form.formState.errors.twitterUrl?.message}>
              <Input
                  placeholder={'Twitter/X profile URL (optional)'}
                  className={'rounded-lg'}
                  {...form.register('twitterUrl')}
                />
            </InputWrapper>
            </div>
          </div>
          <div className={'flex items-center justify-stretch w-full gap-3 mt-6'}>
            <Button
              className={'w-full'}
              type={'submit'}
             
            >
              Save changes
            </Button>
            <Button
              className={'bg-card-foreground w-full'}
              onClick={e => {
                e.stopPropagation()
                e.preventDefault()
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
